import { useToast } from '@chakra-ui/react'
import { MangoAlert } from '~/components/shared/MangoAlert'
import { getFaxToastEvent } from '~/hooks/toast/eventHandlers/faxToastEventHandler'
import { getGeneralToastEvent } from '~/hooks/toast/eventHandlers/generalToastEventHandler'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
  NotificationPayload,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'

import { getContactsToastEvent } from './eventHandlers/contactsToastEventHandler'
import { getVoicemailToastEvent } from './eventHandlers/voicemailToastEventHandler'
import { getWebphoneToastEvent } from './eventHandlers/webphoneToastEventHandler'

export interface ToastEventPayload {
  title: string
  description: string
  disposition: NotificationDisposition
  durationMs?: number
}

const getHandlerToastEvent = (
  payload: NotificationPayload<NotificationDomain>,
) => {
  switch (payload.domain) {
    case NotificationDomain.GENERAL:
      return getGeneralToastEvent(
        payload as NotificationPayload<NotificationDomain.GENERAL>,
      )
    case NotificationDomain.FAX:
      return getFaxToastEvent(
        payload as NotificationPayload<NotificationDomain.FAX>,
      )
    case NotificationDomain.CONTACTS:
      return getContactsToastEvent(
        payload as NotificationPayload<NotificationDomain.CONTACTS>,
      )
    case NotificationDomain.WEBPHONE:
      return getWebphoneToastEvent(
        payload as NotificationPayload<NotificationDomain.WEBPHONE>,
      )
    case NotificationDomain.VOICEMAIL:
      return getVoicemailToastEvent(
        payload as NotificationPayload<NotificationDomain.VOICEMAIL>,
      )
    default:
      return null
  }
}

const defaultToastDurationMs = 3250
const handleNotificationEvent = (
  toast: ReturnType<typeof useToast>,
  eventPayload: NotificationPayload<NotificationDomain>,
) => {
  const toastEvent = getHandlerToastEvent(eventPayload)
  if (!toastEvent) {
    return
  }

  const { title, description, disposition, durationMs } = toastEvent
  toast({
    title,
    description,
    position: 'top-right',
    duration: durationMs ?? defaultToastDurationMs,
    render: ({ onClose }) => {
      return (
        <MangoAlert
          title={title}
          description={description}
          status={disposition}
          onClose={onClose}
          closeOnClick
        />
      )
    },
  })
}

const subscriptionToken = 'toastObserver'
export const useToastAlerts = () => {
  const { subscribe } = useEmitterService()
  const toast = useToast({})
  subscribe({
    subscription: {
      eventType: NotificationEmitterEventType.PROCESS_UPDATED,
      callback: (eventPayload) => handleNotificationEvent(toast, eventPayload),
    },
    subscriptionToken,
  })
}

export const useToastAlert = () => {
  const toast = useToast({})

  const handleToast = (
    status: NotificationDisposition,
    title: string,
    description?: string,
    duration?: number,
    closeOnClick?: boolean,
  ) => {
    toast({
      title: title,
      description: description,
      position: 'top-right',
      duration: duration ?? defaultToastDurationMs,
      render: ({ onClose }) => {
        return (
          <MangoAlert
            title={title}
            description={description}
            status={status}
            onClose={onClose}
            closeOnClick={closeOnClick}
          />
        )
      },
    })
  }

  const handleToastSuccess = (
    title: string,
    description?: string,
    duration?: number,
    closeOnClick?: boolean,
  ) => {
    handleToast(
      NotificationDisposition.Success,
      title,
      description,
      duration,
      closeOnClick,
    )
  }

  const handleToastError = (
    title: string,
    description?: string,
    duration?: number,
    closeOnClick?: boolean,
  ) => {
    handleToast(
      NotificationDisposition.Error,
      title,
      description,
      duration,
      closeOnClick,
    )
  }

  return {
    toastSuccess: handleToastSuccess,
    toastError: handleToastError,
    toast: handleToast,
  }
}
